import { createContext, useEffect, useReducer, useState } from 'react';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import {BACKEND_URL} from "@/constants/globalConstants";
import { validatePluginAccess } from '@/queries/pluginQueries';
import { getMappedOrganizations, LOGIN_SUBROOT_KEY, setLoginSubroot } from 'utils/utils';

const chance = new Chance();

// Thrown when /user/me doesn't send the organizations list: without it the organization of the
// session can't be checked, so the login is refused
const MISSING_ORGANIZATIONS = 'The backend did not provide the organizations of the user';

// The user of the session, with the mapped names of the organizations the backend lets them see
const buildSessionUser = (user, organizations) => {
  const visibleOrganizations = getMappedOrganizations(organizations);
  if (!visibleOrganizations) throw new Error(MISSING_ORGANIZATIONS);
  return { ...user, visibleOrganizations };
};

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// Costruisci dinamicamente l'URL
const backendBaseURL = BACKEND_URL;

const verifyToken = (sessionToken) => {
  if (!sessionToken) {
    return false;
  }
  const decoded = jwtDecode(sessionToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

// A session is a plugin one only when it was opened by loginWithToken: its token is also kept
// as pluginSessionToken, so a later normal login (a different token) is never a plugin session
const isPluginSessionToken = (sessionToken) =>
  !!sessionToken && localStorage.getItem('pluginSessionToken') === sessionToken;

// The plugin_key the plugin opened this tab with: kept in sessionStorage so a reload (F5) of the
// library can validate again with the backend, while other tabs / a pasted URL don't get it
export const getStoredPluginKey = () => sessionStorage.getItem('pluginKey');

const setSession = (sessionToken, { plugin = false, pluginKey } = {}) => {
  if (sessionToken) {
    localStorage.setItem('sessionToken', sessionToken);
    if (plugin) localStorage.setItem('pluginSessionToken', sessionToken);
    else localStorage.removeItem('pluginSessionToken');
    if (!plugin) sessionStorage.removeItem('pluginKey');
    else if (pluginKey) sessionStorage.setItem('pluginKey', pluginKey);
    axios.defaults.headers.common.Authorization = `Bearer ${sessionToken}`;
  } else {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('pluginSessionToken');
    sessionStorage.removeItem('pluginKey');
    // logout / failed login: the next login sets the subroot again
    localStorage.removeItem(LOGIN_SUBROOT_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
};

// The plugin opens this page right after its login, possibly before its socket is connected:
// while the backend answers 409 (plugin not connected) the check is retried for a few seconds.
const PLUGIN_VALIDATION_ATTEMPTS = 10;
const PLUGIN_VALIDATION_RETRY_MS = 1000;

const waitForPluginValidation = async (pluginKey) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await validatePluginAccess(pluginKey);
    } catch (error) {
      if (error?.status !== 409 || attempt >= PLUGIN_VALIDATION_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, PLUGIN_VALIDATION_RETRY_MS));
    }
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isAuthenticated, setAuthenticated] = useState(false);
  // true when the current session was opened by the plugin (loginWithToken)
  const [isPluginSession, setIsPluginSession] = useState(false);
  // true once loginWithToken succeeded in this page load (in memory only: a reload resets it),
  // i.e. the user came through the plugin login, before the plugin opened its socket
  const [pluginLoginDone, setPluginLoginDone] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const sessionToken = window.localStorage.getItem('sessionToken');
        if (sessionToken && verifyToken(sessionToken)) {
          const plugin = isPluginSessionToken(sessionToken);
          setSession(sessionToken, { plugin });
          setIsPluginSession(plugin);

          // Reload of a tab opened by the plugin: validate its plugin_key again, so the library
          // stays open while the plugin is still connected with this session
          const pluginKey = plugin && getStoredPluginKey();
          if (pluginKey) {
            try {
              await waitForPluginValidation(pluginKey);
              setPluginLoginDone(true);
            } catch (error) {
              console.warn('Plugin access check on reload failed', error);
              sessionStorage.removeItem('pluginKey');
            }
          }

          const response = await axios.get(`${backendBaseURL}/user/me`);
          const { user, organizations } = response.data;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: buildSessionUser(user, organizations)
            }
          });
        } else {
          // no session or session expired: a new login is needed
          localStorage.removeItem(LOGIN_SUBROOT_KEY);
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        // e.g. no organizations from the backend: the stored session isn't usable
        if (err?.message === MISSING_ORGANIZATIONS) setSession(null);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  // Opens the session answered by /auth/login: organization is the one it was opened on, the
  // login subroot of the redirects (e.g. to /<organization> after a login from /login)
  const openLoginSession = async ({ session_token, organization }) => {
    setSession(session_token);
    setIsPluginSession(false);
    setPluginLoginDone(false);

    // no session: shown as an error by the login form instead of failing silently
    if(!session_token) {
      setAuthenticated(false);
      localStorage.setItem('isAuthenticated', "false");
      throw new Error('Login failed');
    }

    setAuthenticated(true);
    localStorage.setItem('isAuthenticated', "true");
    localStorage.setItem('sessionToken', session_token);
    setLoginSubroot(organization && `/${organization}`);

    try {
      const meResponse = await axios.get(`${backendBaseURL}/user/me`);
      const { user, organizations } = meResponse.data;

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: buildSessionUser(user, organizations)
        }
      });
    } catch (error) {
      console.error("Errore nel recupero del profilo dopo il login:", error);
      await logout();
      // organizations missing: shown by the login form instead of failing silently
      if (error?.message === MISSING_ORGANIZATIONS) throw error;
    }
  };

  // organization: la subroot da cui si fa login, '' da /login. Il backend
  // verifica che l'utente veda quell'organizzazione, altrimenti risponde con un errore.
  // Da /login con più organizzazioni visibili il backend non apre ancora la sessione: login
  // restituisce { pendingToken, organizations } e si conclude con selectOrganization
  const login = async (email, password, organization = '') => {
    const response = await axios.post(`${backendBaseURL}/auth/login`, { email, password, organization });
    // organizations: [{ mapped_name, original_name }] to choose from (e.g. legaserieb / Lega Serie B)
    const { pending_token, organizations } = response.data;
    if (pending_token) return { pendingToken: pending_token, organizations: organizations ?? [] };

    await openLoginSession(response.data);
    return null;
  };

  // Second step of a login from /login: the organization chosen among the visible ones
  const selectOrganization = async (pendingToken, organization) => {
    const response = await axios.post(`${backendBaseURL}/auth/login`, { pending_token: pendingToken, organization });
    await openLoginSession(response.data);
  };

  // pluginSessionToken is the backend session the plugin got at its own login
  // (/auth/login) and pluginKey the plugin_key of that session: the web app uses that same
  // session only if the backend confirms that pluginKey belongs to it and that the plugin is
  // connected (/plugin/validate), i.e. the page was opened by the plugin.
  // Returns { success: true } on a valid token, or { success: false, reason } otherwise,
  // so callers (e.g. the plugin entry guard) can surface *why* validation failed.
  const loginWithToken = async (pluginSessionToken, pluginKey) => {
    try {
      if (!pluginSessionToken || !pluginKey) {
        const reason = 'No token provided';
        console.warn(`Plugin token check failed: ${reason}`);
        return { success: false, reason };
      }

      const decoded = jwtDecode(pluginSessionToken);
      if (!decoded.exp || decoded.exp <= Date.now() / 1000) {
        const reason = 'Token expired';
        console.warn(`Plugin token check failed: ${reason}`, {
          exp: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : null,
          now: new Date().toISOString()
        });
        return { success: false, reason };
      }

      // The backend verifyMiddleware checks the plugin session (and refreshes its platform
      // token) on every call below: if any fails, the catch below clears the session.
      setSession(pluginSessionToken, { plugin: true, pluginKey });
      await waitForPluginValidation(pluginKey);
      setIsPluginSession(true);
      setPluginLoginDone(true);

      const meResponse = await axios.get(`${backendBaseURL}/user/me`);
      const { user, organizations } = meResponse.data;

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: buildSessionUser(user, organizations)
        }
      });
      localStorage.setItem('isAuthenticated', 'true');
      return { success: true };
    } catch (error) {
      // `axios` here is the shared instance from utils/axios.js, whose response interceptor
      // rewrites errors into a flat { status, message } shape.
      const status = error?.status ?? error?.response?.status;
      let reason;
      if (error?.message === MISSING_ORGANIZATIONS) {
        reason = MISSING_ORGANIZATIONS;
      } else if (error?.name === 'InvalidTokenError') {
        reason = 'Malformed token';
      } else if (status === 409) {
        reason = 'Plugin not connected';
      } else if (status === 403) {
        reason = 'The library can only be opened from the plugin';
      } else if (status) {
        reason = `Server rejected the token (${status})`;
      } else {
        reason = 'Could not reach the backend (network or CORS error)';
      }

      console.error(`Plugin token check failed: ${reason}`, error);
      setSession(null);
      setIsPluginSession(false);
      setPluginLoginDone(false);
      dispatch({ type: LOGOUT });
      return { success: false, reason };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    try {
      await axios.post(`${backendBaseURL}/auth/logout`);
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setSession(null);
      setAuthenticated(false);
      setIsPluginSession(false);
      setPluginLoginDone(false);
      localStorage.removeItem('isAuthenticated');
      dispatch({ type: LOGOUT });
    }
  };

  const localLogout = async () => {
    setSession(null);
    setAuthenticated(false);
    setIsPluginSession(false);
    setPluginLoginDone(false);
    localStorage.removeItem('isAuthenticated');
    dispatch({ type: LOGOUT });
  }

  const resetPassword = async (email) => {
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  const checkUserRoles = (roles) => { return state.user?.roles?.some((userRole) => roles.includes(userRole.name));};

  const checkUserPermissions = (requiredPermissions) => {
    const userPermissions = new Set();
    const user = state.user;

    if (user && Array.isArray(user.roles)) {
      user.roles.forEach(role => {
        if (Array.isArray(role.permissions)) {
          role.permissions.forEach(permission => userPermissions.add(permission));
        }
      });
    }
    return requiredPermissions.every(permission => userPermissions.has(permission));
  }

  return <JWTContext value={{ ...state, login, selectOrganization, loginWithToken, isPluginSession, pluginLoginDone, logout, register, resetPassword, updateProfile, checkUserRoles, checkUserPermissions, localLogout }}>{children}</JWTContext>;
};

export default JWTContext;
